import React, { useState } from "react";

/* Two-step verification building blocks (Salem 2026-10-01, after a staff mailbox was phished),
   shared by the sign-in page (setup forced for managers/admins) and the Account page. */

const APPS = "Microsoft Authenticator, Google Authenticator or 1Password";

/* The setup screen: scan the QR (or type the key), then enter the 6-digit code. */
export function QrSetup({ data, compact }) {
  const [showKey, setShowKey] = useState(false);
  if (!data) return <div className="muted small">Preparing…</div>;
  return (
    <div className="tfa-setup">
      <ol className="tfa-steps">
        <li>Open an authenticator app on your phone — {APPS}.</li>
        <li>Add an account and scan this code{data.qrSvg ? "" : " (or type the key below)"}.</li>
        <li>Enter the 6-digit code the app shows.</li>
      </ol>
      <div className="tfa-qrrow" style={compact ? { justifyContent: "flex-start" } : undefined}>
        {data.qrSvg ? (
          // Server-made SVG of our own otpauth:// URL (segno) — black on white so phones read it in any theme.
          <div className="tfa-qr" aria-label="QR code for your authenticator app"
            dangerouslySetInnerHTML={{ __html: data.qrSvg }} />
        ) : null}
      </div>
      {(showKey || !data.qrSvg) ? (
        <div className="tfa-key">
          <div className="muted small">Can't scan? Enter this key in the app (time-based):</div>
          <code>{data.secret}</code>
          <div className="muted small">Account: {data.account} · Issuer: {data.issuer}</div>
        </div>
      ) : (
        <button type="button" className="tfa-linkbtn" onClick={() => setShowKey(true)}>
          Can't scan the code? Show the key to type in
        </button>
      )}
    </div>
  );
}

/* Shown once, straight after setup (or after making new ones). */
export function BackupCodes({ codes, email }) {
  const [copied, setCopied] = useState(false);
  const text = `RepIQ backup codes${email ? ` for ${email}` : ""}\n` +
    `Each code works once. Keep them somewhere safe (not in your email).\n\n` + codes.join("\n") + "\n";
  const copy = () => {
    try {
      navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    } catch { /* ignore */ }
  };
  const download = () => {
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "repiq-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
  };
  return (
    <div className="tfa-backup">
      <p className="small" style={{ margin: "0 0 10px" }}>
        <b>Save these backup codes.</b> If you lose your phone, each one gets you in once.
        They won't be shown again. Keep them in a password manager or print them — not in your email.
      </p>
      <div className="tfa-codes">{codes.map((c) => <code key={c}>{c}</code>)}</div>
      <div className="flex" style={{ gap: 8, marginTop: 10 }}>
        <button type="button" className="btn btn-outline btn-sm" onClick={copy}>{copied ? "Copied ✓" : "Copy"}</button>
        <button type="button" className="btn btn-outline btn-sm" onClick={download}>Download .txt</button>
      </div>
    </div>
  );
}

/* A 6-digit (or backup-code) box. */
export function CodeBox({ value, onChange, backup, className = "input", autoFocus = true }) {
  return (
    <input className={className} value={value} autoFocus={autoFocus}
      inputMode={backup ? "text" : "numeric"} autoComplete="one-time-code"
      maxLength={backup ? 12 : 7} spellCheck={false}
      placeholder={backup ? "xxxx-xxxx" : "123 456"}
      style={{ letterSpacing: backup ? 2 : 6, fontVariantNumeric: "tabular-nums" }}
      onChange={(e) => onChange(backup ? e.target.value : e.target.value.replace(/[^0-9 ]/g, ""))} />
  );
}
